import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import i18n from 'i18next';
import { toast } from 'sonner';
import { useAppStore } from './appStore';
import { AuthMode, AuthUser, Locale } from '../types';
import { resetAllProductRelatedStores } from '../utils';
import { useArrivalReceiptStore } from './arrivalReceiptStore';
import { isAdminGroup, resolveRoles } from '../utils/permissions';
import { getMe, updateMyLocale } from '../services/api/auth';
import { setSessionExpiredHandler } from '../services/api';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  stationId: string | null;
  authMode: AuthMode | null;
  isUpdatingLocale: boolean;

  setStationId: (stationId: string | null) => void;
  setAuthMode: (authMode: AuthMode | null) => void;
  restoreSession: () => Promise<void>;
  setAuthenticatedUser: (user: AuthUser) => void;
  syncLocale: () => Promise<void>;
  updateLocale: (locale: Locale) => Promise<void>;
  clearAuthState: () => void;
  logout: () => Promise<void>;
  expireSession: () => boolean;
}

const applyLocale = (locale: Locale) => {
  i18n.changeLanguage(locale);
  useAuthStore.setState((state) => ({
    user: state.user ? { ...state.user, locale } : null,
  }));
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      status: 'loading',
      user: null,
      stationId: null,
      authMode: null,
      isUpdatingLocale: false,

      setStationId: (stationId) => set({ stationId }),
      setAuthMode: (authMode) => set({ authMode }),

      setAuthenticatedUser: (user) => {
        set({
          user,
          status: 'authenticated',
        });
        void get().syncLocale();
      },

      syncLocale: async () => {
        try {
          const profile = await getMe();
          applyLocale(profile.locale ?? 'es');
        } catch {
          // keep whichever locale is currently active if the profile call fails
        }
      },

      updateLocale: async (locale) => {
        const { user, isUpdatingLocale } = get();
        if (!user || isUpdatingLocale || locale === user.locale) return;

        set({ isUpdatingLocale: true });
        try {
          const profile = await updateMyLocale(locale);
          applyLocale(profile.locale ?? locale);
          toast.success(i18n.t('userProfile.languageUpdated'));
        } catch (error) {
          const err = error as { message?: string };
          toast.error(err.message || i18n.t('userProfile.failedToUpdateLanguage'));
        } finally {
          set({ isUpdatingLocale: false });
        }
      },

      clearAuthState: () =>
        set({
          user: null,
          status: 'unauthenticated',
          stationId: null,
          authMode: null,
        }),

      restoreSession: async () => {
        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;

          if (!idToken) {
            set({
              user: null,
              status: 'unauthenticated',
            });
            return;
          }

          const payload = idToken.payload as Record<string, unknown>;
          const groups = (payload['cognito:groups'] as string[]) ?? [];
          const username = (payload['cognito:username'] ||
            payload.name ||
            payload.email ||
            '') as string;
          const user: AuthUser = {
            id: (payload.name as string) || (payload.email as string) || '',
            username,
            email: (payload.email as string) || undefined,
            name: (payload.name as string) || (payload.email as string) || '',
            sub: (payload.sub as string) || '',
            roles: resolveRoles(groups),
            isAdmin: isAdminGroup(groups),
            locale: 'es',
          };

          set({
            user,
            status: 'authenticated',
          });
          void get().syncLocale();
        } catch {
          set({
            user: null,
            status: 'unauthenticated',
          });
        }
      },

      logout: async () => {
        try {
          await signOut();
        } catch {
          // ignore remote sign-out failure, still clear local state
        }

        set({
          user: null,
          status: 'unauthenticated',
          stationId: null,
          authMode: null,
        });
        i18n.changeLanguage('es');

        resetAllProductRelatedStores();
        useAppStore.getState().reset();
        useArrivalReceiptStore.getState().setCurrentArrivalReceipt(null);
      },

      expireSession: () => {
        if (get().status !== 'authenticated') return false;

        set({ status: 'unauthenticated' });
        toast.error(i18n.t('toast.sessionExpired'), { id: 'session-expired' });
        void get().logout();
        return true;
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        stationId: state.stationId,
        authMode: state.authMode,
      }),
    }
  )
);

setSessionExpiredHandler(() => useAuthStore.getState().expireSession());

Hub.listen('auth', ({ payload }) => {
  if (payload.event === 'tokenRefresh_failure') {
    useAuthStore.getState().expireSession();
  }
});
